// solo/Services.jsx - With patch test requirement option
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RetailFlagsManager from './RetailFlagsManager';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import api from '../axiosConfig';
const DAY_CODES = [
  { code: 'mon', label: 'Mon' },
  { code: 'tue', label: 'Tue' },
  { code: 'wed', label: 'Wed' },
  { code: 'thu', label: 'Thu' },
  { code: 'fri', label: 'Fri' },
  { code: 'sat', label: 'Sat' },
  { code: 'sun', label: 'Sun' },
];
const ALL_DAYS = DAY_CODES.map(d => d.code);

const Services = () => {
  const navigate = useNavigate();
  
  // State
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 30,
    processing_time: 0,
    default_price: 0,
    requires_patch_test: false,
    allowed_days: ALL_DAYS,
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch services
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/services');
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      showSnackbar('Failed to load services', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Dialog handlers
  const handleOpenDialog = (service = null) => {
    if (service) {
      setFormData({
        name: service.name,
        description: service.description || '',
        duration: service.duration,
        processing_time: service.processing_time || 0,
        default_price: service.default_price,
        requires_patch_test: service.requires_patch_test || false,
        allowed_days: Array.isArray(service.allowed_days) && service.allowed_days.length > 0
          ? service.allowed_days
          : ALL_DAYS,
      });
      setSelectedService(service);
    } else {
      setFormData({
        name: '',
        description: '',
        duration: 30,
        processing_time: 0,
        default_price: 0,
        requires_patch_test: false,
        allowed_days: ALL_DAYS,
      });
      setSelectedService(null);
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      name: '',
      description: '',
      duration: 30,
      processing_time: 0,
      default_price: 0,
      requires_patch_test: false,
      allowed_days: ALL_DAYS,
    });
    setSelectedService(null);
    setFormErrors({});
  };

  // Form change handler
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Service name is required';
    }
    
    if (!formData.duration || formData.duration < 5) {
      errors.duration = 'Duration must be at least 5 minutes';
    }
    
    if (formData.processing_time < 0) {
      errors.processing_time = 'Processing time cannot be negative';
    }
    
    if (formData.default_price < 0) {
      errors.default_price = 'Price cannot be negative';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      if (selectedService?.id) {
        // Update existing
        await api.put(`/services/${selectedService.id}`, formData);
        showSnackbar('Service updated successfully!');
      } else {
        // Create new
        await api.post('/services', formData);
        showSnackbar('Service created successfully!');
      }
      
      handleCloseDialog();
      fetchServices();
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || 'Failed to save service',
        'error'
      );
    }
  };

  // Delete service
  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/services/${serviceId}`);
      showSnackbar('Service deleted successfully!');
      fetchServices();
    } catch (error) {
      showSnackbar(
        error.response?.data?.error || 'Failed to delete service',
        'error'
      );
    }
  };

  // Format duration display
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Compact Header */}
      <Box 
        sx={{ 
          bgcolor: 'white', 
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Services
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Service
        </Button>
      </Box>

      {/* Services Table - Full Height */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Typography>Loading services...</Typography>
          ) : services.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No services yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first service to get started with appointments
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create First Service
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Service Name</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                    <TableCell><strong>Processing Time</strong></TableCell>
                    <TableCell><strong>Total Time</strong></TableCell>
                    <TableCell><strong>Price</strong></TableCell>
                    <TableCell><strong>Patch Test</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service) => (
                    <TableRow key={service.id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {service.name}
                        </Typography>
                        {service.description && (
                          <Typography variant="caption" color="text.secondary">
                            {service.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatDuration(service.duration)} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {service.processing_time > 0 ? (
                          <Chip 
                            label={formatDuration(service.processing_time)} 
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDuration(service.duration + (service.processing_time || 0))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          £{parseFloat(service.default_price).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {service.requires_patch_test ? (
                          <Chip 
                            icon={<ScienceIcon />}
                            label="Required" 
                            size="small"
                            color="warning"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(service)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(service.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedService ? 'Edit Service' : 'New Service'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
                placeholder="e.g., Cut & Blow Dry, Full Head Highlights"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={2}
                placeholder="Optional description of the service"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Active Work Duration (minutes)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleFormChange}
                error={!!formErrors.duration}
                helperText={formErrors.duration || 'Time spent actively working'}
                required
                inputProps={{ min: 5, step: 5 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Processing Time (minutes)"
                name="processing_time"
                type="number"
                value={formData.processing_time}
                onChange={handleFormChange}
                error={!!formErrors.processing_time}
                helperText={formErrors.processing_time || 'e.g., color development time'}
                inputProps={{ min: 0, step: 5 }}
              />
            </Grid>
            
            {(formData.duration + formData.processing_time) > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Total time blocked: {formatDuration(parseInt(formData.duration) + parseInt(formData.processing_time))}</strong>
                  <br />
                  <Typography variant="caption">
                    • Active work: {formatDuration(formData.duration)}
                    {formData.processing_time > 0 && (
                      <> • Processing: {formatDuration(formData.processing_time)} (other clients can be booked during this time)</>
                    )}
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Default Price (£)"
                name="default_price"
                type="number"
                value={formData.default_price}
                onChange={handleFormChange}
                error={!!formErrors.default_price}
                helperText={formErrors.default_price || 'Can be adjusted per appointment'}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requires_patch_test}
                    onChange={handleFormChange}
                    name="requires_patch_test"
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScienceIcon color="warning" fontSize="small" />
                    <span>Requires patch test (Chemical Services as per Manufacturers Instructions)</span>
                  </Box>
                }
              />
              {formData.requires_patch_test && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Clients will need a valid patch test before booking this service
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Available on
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DAY_CODES.map(({ code, label }) => {
                  const selected = formData.allowed_days.includes(code);
                  return (
                    <Chip
                      key={code}
                      label={label}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          allowed_days: selected
                            ? prev.allowed_days.filter(d => d !== code)
                            : [...prev.allowed_days, code],
                        }));
                      }}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer', minWidth: 56 }}
                    />
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {formData.allowed_days.length === 0
                  ? 'No days selected — clients won\'t be able to book this service online. (Tip: leaving all days selected means "any day.")'
                  : formData.allowed_days.length === 7
                    ? 'Available every day online. You can still book this service manually on any day.'
                    : `Online clients can book this on: ${DAY_CODES.filter(d => formData.allowed_days.includes(d.code)).map(d => d.label).join(', ')}. Manual bookings aren't restricted.`}
              </Typography>
            </Grid>
            {/* ↓ NEW BLOCK GOES HERE ↓ */}
            {selectedService?.id && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <RetailFlagsManager serviceId={selectedService.id} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedService ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;